<template>
  <!-- Loading -->
  <div class="batch-detail-loader" v-if="isLoading">
    <div class="progress">
      <div class="indeterminate"></div>
    </div>
  </div>

  <!-- Display claims -->
  <template v-else>
    <section class="finance-batch-detail">
      <div class="batch-detail-head">
        <div>
          <label class="active teal-text">Payment Batch</label>
          <h6>Provider Payment Schedule</h6>
          <p>{{ clusters.length }} {{ clusters.length === 1 ? "provider bill" : "provider bills" }} in this batch.</p>
        </div>

        <button type="button" class="batch-download-btn" @click="handleDownload()" title="download batch">
          <i class="material-icons">download</i>
          <span>Download</span>
        </button>
      </div>

      <div class="batch-table-wrap">
        <table class="responsive-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Provider</th>
              <th>Account</th>
              <th>Period</th>
              <th>Approved</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <!-- Claims Summary -->
            <template v-for="(cluster, index) in clusters" :key="cluster.id">
              <tr class="batch-provider-row clickable" @click.prevent="handleShow(cluster)">
                <td>{{ index + 1 }}</td>
                <td>
                  <strong>{{ capWords(cluster.provider.name) }}</strong>
                  <span
                    class="claim-context-badge"
                    :class="claimContext(cluster) ? `is-${claimContext(cluster).key}` : null"
                    v-if="claimContext(cluster)"
                  >
                    {{ claimContext(cluster).label }}
                  </span>
                </td>
                <td>
                  <span>{{ cluster.provider.bank ? capWords(cluster.provider.bank) : "No bank listed" }}</span>
                  <small>{{ cluster.provider.account || "No account listed" }}</small>
                </td>
                <td>
                  <span>{{ toMonthYear(cluster.month_year) }}</span>
                  <small>{{ cluster.scheme.name }}</small>
                </td>
                <td>
                  <strong>₦{{ clusterApprovedTotal(cluster) }}</strong>
                </td>
                <td v-if="batchStage == 'c'">
                  <span class="batch-row-status" title="completed">
                    <i class="material-icons">check</i>
                  </span>
                </td>
                <td @click.stop.prevent="handleReBatch(cluster)" v-else>
                  <button
                    type="button"
                    class="batch-row-action remove-item"
                    title="remove and re-batch"
                    v-if="clusters.length > 1"
                  >
                    <i class="material-icons">low_priority</i>
                  </button>
                  <button type="button" class="batch-row-action is-blocked" title="re-batch blocked" v-else>
                    <i class="material-icons">do_not_disturb</i>
                  </button>
                </td>
              </tr>

              <!-- Encounter details -->
              <template v-if="isClusterOpen(cluster)">
                <tr class="encounter-row cluster-detail-message" v-if="clusterDetailLoading[cluster.id]">
                  <td></td>
                  <td colspan="5">
                    <div class="progress cluster-detail-progress">
                      <div class="indeterminate"></div>
                    </div>
                    <span>Loading provider encounters</span>
                  </td>
                </tr>
                <tr class="encounter-row cluster-detail-message is-error" v-else-if="clusterDetailErrors[cluster.id]">
                  <td></td>
                  <td colspan="5">
                    <span>{{ clusterDetailErrors[cluster.id] }}</span>
                  </td>
                </tr>
                <tr class="encounter-row" v-else v-for="encounter in cluster.encounters || []" :key="encounter.id">
                  <td></td>
                  <td colspan="3">
                    <span>{{ capWords(encounter.name) }}</span>
                  </td>
                  <td>
                    <strong>₦{{ totalApproved(encounter) }}</strong>
                  </td>
                  <td></td>
                </tr>
                <tr
                  class="encounter-row cluster-detail-message"
                  v-if="!clusterDetailLoading[cluster.id] && !clusterDetailErrors[cluster.id] && !(cluster.encounters || []).length"
                >
                  <td></td>
                  <td colspan="5">
                    <span>No encounter records are available for this provider bill.</span>
                  </td>
                </tr>
              </template>
            </template>

            <!-- Final total -->
            <tr class="batch-total-row">
              <td colspan="4">Total approved</td>
              <td>
                <strong>₦{{ batchTotalApproved(clusters) }}</strong>
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Submit -->
      <div class="batch-action-strip" v-if="batchStage != 'c'">
        <div>
          <strong>Finalize payment</strong>
          <span>Mark this batch as paid after confirming the provider payment schedule.</span>
        </div>
        <button
          type="button"
          class="batch-submit-btn"
          :class="{ disabled: isTalking || isSubmitting }"
          @click="handleSubmit('done')"
        >
          <i class="material-icons">check_circle</i>
          {{ isSubmitting ? "Updating Payment Status" : "Mark Batch As Paid" }}
        </button>
      </div>
    </section>
  </template>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import getItems from "@/composables/getItems";
import putItem from "@/composables/putItem";
import {
  toMonthYearFx,
  capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";
import {
  totalApprovedFx,
  grossTotalApprovedFx,
} from "@/composables/calFunction/cal";
import {
  downloadExcelFx,
} from "@/composables/reportFunction/downloader";
import { claimBatchContext } from "@/composables/claimBatchContext";
import M from "materialize-css";


export default {
  name: "FinanceSingleBatchComponent",
  props: ["batchId"],
  emits: ["reset"],
  setup(props, context) {
    const response = ref(null);
    const currCluster = ref(null);
    const batchStage = ref(null);
    const error = ref(null);
    const clusterDetailLoading = ref({});
    const clusterDetailErrors = ref({});
    const isTalking = ref(0);
    const isReady = ref(false);
    const isSubmitting = ref(false);
    const isLoading = ref(true);

    const moneyNumber = (val) => {
      return Number(val || 0);
    };

    const formatMoney = (val) => {
      return moneyNumber(val)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const clusterApprovedTotalNumber = (cluster) => {
      if (Array.isArray(cluster?.encounters)) {
        return moneyNumber(grossTotalApprovedFx(cluster.encounters, true));
      }

      return moneyNumber(cluster?.approved_total);
    };

    // total calculations (clustered approved)
    const batchTotalApproved = (clusters) => {
      const total = (clusters || []).reduce((sum, cluster) => {
        return sum + clusterApprovedTotalNumber(cluster);
      }, 0);

      return formatMoney(total);
    };

    // total calculations (gross approved)
    const grossTotalApproved = (encounters, db) => {
      return grossTotalApprovedFx(encounters, db);
    };

    // total calculations (approved)
    const totalApproved = (encounter) => {
      return totalApprovedFx(encounter);
    };

    const clusterApprovedTotal = (cluster) => {
      return formatMoney(clusterApprovedTotalNumber(cluster));
    };

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    const claimContext = (cluster) => {
      return claimBatchContext(cluster);
    };

    const claimExportLabel = (cluster) => {
      const context = claimContext(cluster);

      if (!context) {
        return "Main";
      }

      return context.label.replace(/\s+[A-Z]$/, "");
    };

    // convert to Month & Year
    const toMonthYear = (val) => {
      return toMonthYearFx(val);
    };

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
          // set batchStage
          batchStage.value = response.value.stage;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    const getClusterFromApi = async (cluster) => {
      if (Array.isArray(cluster?.encounters)) {
        return;
      }

      clusterDetailLoading.value = {
        ...clusterDetailLoading.value,
        [cluster.id]: true,
      };
      clusterDetailErrors.value = {
        ...clusterDetailErrors.value,
        [cluster.id]: null,
      };

      const { res, err } = await getItems(
        `cluster/finance/vetted/${props.batchId}/cluster/${cluster.id}`
      );

      if (res.value) {
        const detail = res.value.data;
        const index = response.value?.clusters?.findIndex((x) => x.id === cluster.id);

        if (index >= 0) {
          response.value.clusters[index] = {
            ...response.value.clusters[index],
            ...detail,
          };
        }
      } else {
        clusterDetailErrors.value = {
          ...clusterDetailErrors.value,
          [cluster.id]: err.value?.message || "Unable to load provider encounters.",
        };
      }

      clusterDetailLoading.value = {
        ...clusterDetailLoading.value,
        [cluster.id]: false,
      };
    };

    // starter
    onMounted(() => {
      // get started
      getItemsFromApi(`cluster/finance/vetted?q=${props.batchId}`);
    });

    // show claims
    const handleShow = async (val) => {
      if (currCluster.value == val.id) {
        currCluster.value = null;
      } else {
        currCluster.value = val.id;
        await getClusterFromApi(val);
      }
    };

    const isClusterOpen = (cluster) => {
      return currCluster.value == cluster.id;
    };

    // reBatch
    const handleReBatch = async (val) => {
      if (clusters.value.length > 1) { // cannot re-batch single record

        // talk care of multiple clicks
        isTalking.value++;

        // update first (twitter style)
        response.value.clusters = response.value.clusters.filter(
          (x) => x.id != val.id
        );

        // update server
        let newVal = [];

        // put update
        const { res, err } = await putItem(
          "cluster/finance/rebatch",
          val.id,
          newVal
        );

        if (res.value) {
          // send reset value to parent
          context.emit("reset", response.value);
          // null cluster
          if (currCluster.value == val.id) {
            currCluster.value = null;
          }
          // talk care of multiple clicks
          isTalking.value--;
        } else {
          error.value = err.value;
        }
      }
    };

    // review
    const handleSubmit = async () => {
      // start
      isSubmitting.value = true;

      // update server
      let newVal = {
        stage: "c", // marked as paid (complete)
      };

      // put update
      const { res, err } = await putItem(
        "cluster/finance/review",
        props.batchId,
        newVal
      );

      if (res.value) {
        // notify (Toast)
          M.toast({
            html: "Payment Completed <i class='material-icons'>check_circle</i>", // Toast content
            classes: 'green', // Add custom classes // green rounded
            displayLength: 5000, // Duration in milliseconds
            inDuration: 300, // Transition in duration
            outDuration: 300, // Transition out duration
          });
        // send reset value to parent
        context.emit("reset", "reload");
      } else {
        error.value = err.value;
      }

      // end submit
      isSubmitting.value = false;
    };


    // download
    const handleDownload = () => {
      let data = [];

      // top
      clusters.value.forEach((x, y) => {

        // all
        data.push({
          No: y + 1,
          Provider: capWords(x.provider.name),
          Claim: claimExportLabel(x),
          Account: `${x.provider.bank ? capWords(x.provider.bank) : ""} ${x.provider.account ? x.provider.account : ""}`,
          Period: `${toMonthYear(x.month_year)} ${x.scheme.name} Claims`,
          Approved: clusterApprovedTotal(x),
        })
        
      })

      // space
      data.push({})

      // final
      data.push({
        No: "",
        Provider: "",
        Account: "",
        Period: "Total",
        Approved: batchTotalApproved(clusters.value),
      })

      // fire function
      return downloadExcelFx(data, `LifeWORTH_HMO_Payment_Batch_${props.batchId}`);
    }


    // output results
    const clusters = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.clusters || [];
      }
    });


    return {
      handleShow,
      isClusterOpen,
      handleReBatch,
      handleSubmit,
      handleDownload,
      capWords,
      claimContext,
      batchTotalApproved,
      totalApproved,
      clusterApprovedTotal,
      grossTotalApproved,
      toMonthYear,
      isReady,
      isTalking,
      isLoading,
      isSubmitting,
      batchStage,
      currCluster,
      clusterDetailLoading,
      clusterDetailErrors,
      error,
      clusters,
    };
  },
};
</script>

<style scoped>
.batch-detail-loader {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-top: 0;
  border-radius: 0 0 6px 6px;
  overflow: hidden;
}

.progress {
  margin: 0;
}

.clickable {
  cursor: pointer;
}

.finance-batch-detail {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: 0 16px 32px -26px rgba(38, 50, 56, 0.78);
  margin-top: 8px;
  overflow: hidden;
}

.batch-detail-head {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 18px 20px;
}

.batch-detail-head h6 {
  color: #263238;
  font-size: 1rem;
  font-weight: 600;
  margin: 4px 0 0;
}

.batch-detail-head p {
  color: #607d8b;
  margin: 6px 0 0;
}

.batch-download-btn,
.batch-row-action,
.batch-submit-btn {
  align-items: center;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  justify-content: center;
}

.batch-download-btn {
  background: #fafafa;
  color: #455a64;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
}

.batch-download-btn i {
  color: #607d8b;
  font-size: 19px;
}

.batch-table-wrap {
  overflow-x: auto;
  padding: 0 18px 14px;
}

table {
  margin: 0;
}

thead {
  color: #607d8b;
  font-size: 0.78rem;
  text-transform: uppercase;
}

td,
th {
  border-bottom: 1px solid #eeeeee;
  padding: 13px 10px;
}

td {
  color: #455a64;
}

td strong {
  color: #263238;
  font-weight: 700;
}

td small,
td span {
  display: block;
}

td small {
  color: #78909c;
  margin-top: 2px;
}

.batch-provider-row:hover {
  background: #fafafa;
}

.claim-context-badge {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
  margin-left: 6px;
  padding: 5px 7px;
  text-transform: uppercase;
  vertical-align: middle;
}

.claim-context-badge.is-supplementary {
  background: #e8f5e9;
  color: #2e7d32;
}

.claim-context-badge.is-reconciliation {
  background: #fff3e0;
  color: #ef6c00;
}

.encounter-row {
  background: #fafafa;
}

.encounter-row td {
  border-bottom-color: #f2f2f2;
  color: #607d8b;
  padding-bottom: 10px;
  padding-top: 10px;
}

.encounter-row span {
  color: #37474f;
  font-weight: 600;
}

.encounter-row strong {
  color: #00897b;
}

.cluster-detail-message td {
  color: #607d8b;
  font-size: 0.9rem;
  font-weight: 600;
}

.cluster-detail-message.is-error td,
.cluster-detail-message.is-error span {
  color: #c62828;
}

.cluster-detail-progress {
  background: #e0f2f1;
  display: inline-block;
  height: 4px;
  margin: 0 12px 0 0;
  vertical-align: middle;
  width: 150px;
}

.batch-row-action {
  background: #ffffff;
  color: #607d8b;
  height: 34px;
  padding: 0;
  width: 34px;
}

.batch-row-action i,
.batch-row-status i {
  font-size: 19px;
}

.batch-row-action:hover {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #c62828;
}

.batch-row-action.is-blocked {
  background: #fafafa;
  color: #b0bec5;
  cursor: not-allowed;
}

.batch-row-action.is-blocked:hover {
  border-color: #eeeeee;
}

.batch-row-status {
  align-items: center;
  background: #eceff1;
  border-radius: 6px;
  color: #455a64;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  width: 30px;
}

.batch-total-row {
  background: #f5f7f8;
}

.batch-total-row td {
  border-bottom: 0;
  color: #263238;
  font-weight: 700;
}

.batch-total-row strong {
  color: #1b5e20;
  font-size: 1.08rem;
}

.batch-action-strip {
  align-items: center;
  background: #fafafa;
  border-top: 1px solid #eeeeee;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 16px 18px;
}

.batch-action-strip strong,
.batch-action-strip span {
  display: block;
}

.batch-action-strip strong {
  color: #263238;
}

.batch-action-strip span {
  color: #607d8b;
  font-size: 0.9rem;
  margin-top: 2px;
}

.batch-submit-btn {
  background: #00897b;
  border-color: #00897b;
  color: #ffffff;
  gap: 8px;
  min-height: 40px;
  padding: 0 16px;
}

.batch-submit-btn.disabled {
  opacity: 0.55;
  pointer-events: none;
}

.batch-submit-btn i {
  font-size: 19px;
}

@media only screen and (max-width: 600px) {
  .batch-detail-head,
  .batch-action-strip {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
  }

  .batch-detail-head h6 {
    font-size: 1.18rem;
    line-height: 1.25;
  }

  .finance-batch-detail {
    border-radius: 6px;
  }

  .batch-table-wrap {
    overflow-x: auto;
  }

  .batch-table-wrap table {
    min-width: 720px;
  }

  .batch-submit-btn,
  .batch-download-btn {
    width: 100%;
  }
}
</style>
